// MakerCutt brand mark — inline SVG so it scales crisply at any size.
//
//   <BrandIcon />     — wireframe open-box icon (matches the favicon).
//                       Pure cyan line art, no background tile, so it
//                       sits cleanly inside the dark header AND on
//                       light tabs.
//   <BrandWordmark>   — full lockup: icon + "MakerCutt" wordmark in
//                       Orbitron + the brand slogan "create.cutt.build"
//                       with `.cutt.` highlighted in cyan to mirror
//                       the double-t in the brand name.

export function BrandIcon({ size = 36, className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      role="img"
      aria-label="MakerCutt"
    >
      <g
        fill="none"
        stroke="#22d3ee"
        strokeWidth="2.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* back-left wall (lifted flap) */}
        <polygon points="12,30 32,20 32,32 12,42" />
        {/* back-right wall (lifted flap) */}
        <polygon points="52,30 32,20 32,32 52,42" />
        {/* front-left flap (open) */}
        <polygon points="12,42 32,52 32,40 12,30" />
        {/* front-right flap (open) */}
        <polygon points="52,42 32,52 32,40 52,30" />
        {/* inner floor (rhombus seen from above) */}
        <polygon points="32,32 22,36 32,40 42,36" />
      </g>
    </svg>
  );
}

// Brand slogan is intentionally fixed in English across every locale —
// it's a brand signature (think "Just Do It"), not a translatable
// piece of UI copy. The dot-notation evokes a CLI / parametric API
// call, which fits the CAD/fab-lab vibe.
export const BRAND_SLOGAN_PARTS = [
  { text: 'create.', accent: false },
  { text: 'cutt.',   accent: true  },
  { text: 'build',   accent: false },
];

export function BrandWordmark({ className = '' }) {
  return (
    <div className={`flex items-center gap-2 sm:gap-2.5 min-w-0 ${className}`}>
      {/* Icon shrinks slightly on phones to free horizontal space for
          the rest of the header (idioma, donate, upgrade, hamburger). */}
      <span className="hidden sm:inline-block">
        <BrandIcon size={36} />
      </span>
      <span className="sm:hidden">
        <BrandIcon size={28} />
      </span>
      <div className="flex flex-col leading-none min-w-0">
        {/*
          Wordmark: "Maker" in light weight, "Cutt" in bold cyan to
          emphasize the double-t. Orbitron is the futuristic display
          face that ties the brand to its CAD/CAM / sci-fi positioning.
          Scales down on phones so the header never overflows.
        */}
        <span
          className="text-[18px] sm:text-[22px] tracking-[0.04em] text-zinc-50 font-medium whitespace-nowrap"
          style={{ fontFamily: "'Orbitron', system-ui, sans-serif" }}
        >
          Maker<span className="text-cyan-400 font-bold">Cutt</span>
        </span>
        {/*
          Slogan locked in English: create.cutt.build with the middle
          token in cyan to mirror the wordmark and tie the system
          together visually. Hidden on the smallest screens to keep
          the header tight; reappears at sm+.
        */}
        <span
          className="hidden sm:inline text-[10px] tracking-[0.18em] text-zinc-400 font-mono mt-1 whitespace-nowrap"
          style={{ fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, monospace" }}
        >
          {BRAND_SLOGAN_PARTS.map((part, i) => (
            <span key={i} className={part.accent ? 'text-cyan-400' : ''}>
              {part.text}
            </span>
          ))}
        </span>
      </div>
    </div>
  );
}

export default BrandWordmark;
